/**
 * The two hooks a page that plays rounds needs from the feed and the
 * microphone: record each round, and know when a round is over.
 *
 * Moved here unchanged from `App.tsx` so the study page's game view can use
 * the same ones as the friends-mode room page — one recording path and one
 * round-end rule, whichever page the round is played on.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RoomPhase } from '@overcooked/shared'
import type { ChunkSink } from '../audio/recorderCore'
import { createAckedSender, recorderStep, withSession } from '../audio/recorderCore'
import { useAudioRecorder } from '../audio/useAudioRecorder'
import { createBeepBus } from '../audio/syncBeep'
import type { BeepBus } from '../audio/syncBeep'
import type { EventFeed, FeedSnapshot } from '../feed/feed'
import type { Connection, VoiceLinkState } from '../net/socket'

/**
 * The audio half of a session: record the round, and mark its zero.
 *
 * Both halves hang off one `MediaStream` that is the microphone mixed with a
 * beep bus, so the tone the server asks for at tick 0 lands *in the file*
 * rather than only in the room — which is what makes the anchor survive a pair
 * wearing headphones (see `audio/syncBeep.ts`).
 *
 * Recording is bracketed by the room phase rather than by a button. A round
 * that has started is a round being recorded; a round that has ended sends its
 * accounting and stops. There is nothing for a participant to forget to press.
 */
export function useSessionRecording(
  feed: EventFeed,
  micStream: MediaStream | null,
  phase: RoomPhase,
  sessionId: string | null,
  connection: React.MutableRefObject<Connection | null>,
): void {
  const [bus, setBus] = useState<BeepBus | null>(null)
  const beepsRef = useRef(0)

  /**
   * The round being recorded, remembered rather than read.
   *
   * Everything a recording sends after the whistle — the final chunk, which only
   * exists once `recorder.stop()` fires `ondataavailable`, and the `audioDone`
   * accounting that is the entire point of the integrity cross-check — is sent
   * while the room may already be sitting in the lobby with a new `sessionId`
   * waiting for another go. Saying which round the bytes belong to is what lets
   * the server hold the finished recording open for a moment without any chance
   * of splicing this round's tail into the next round's file.
   */
  const sessionRef = useRef<string | null>(sessionId)
  if (phase === 'playing' && sessionId) sessionRef.current = sessionId

  useEffect(() => {
    if (!micStream) return
    const next = createBeepBus(micStream)
    setBus(next)
    return () => {
      next.close()
      setBus(null)
    }
  }, [micStream])

  /**
   * Every chunk is kept until the server acknowledges it, and resent if it is
   * not — a socket blip used to drop every chunk sent during it, and the
   * buffered ones were refused on arrival because they beat the page's own
   * rejoin to the server. See `createAckedSender`.
   */
  const sender = useMemo(
    () =>
      createAckedSender({
        emit: (payload, ack) => {
          const live = connection.current
          // No socket, or one that is between connections: the chunk stays
          // pending and the resend pass sends it once the seat is back.
          if (live && live.connected()) live.sendAudioChunk(payload, ack)
        },
      }),
    [connection],
  )
  useEffect(() => {
    const timer = setInterval(() => sender.resendDue(), RESEND_PASS_MS)
    return () => clearInterval(timer)
  }, [sender])

  const onChunk = useCallback<ChunkSink>(
    withSession((payload) => sender.send(payload), () => sessionRef.current),
    [sender],
  )
  const recorder = useAudioRecorder(bus?.stream ?? null, onChunk)

  const start = recorder.start
  const stop = recorder.stop
  const recording = recorder.recording
  /** Whether the run being recorded carries the sync beep. */
  const syncBeepRef = useRef(true)

  useEffect(() => {
    const step = recorderStep({ phase, haveStream: Boolean(bus), recording })
    if (step === 'start' && bus) {
      void (async () => {
        // A suspended graph passes no samples, so a recorder on the bus would
        // record nothing. Try to start it; if the browser will not have it yet
        // (a reload, before any click or key; Safari outside a gesture),
        // record the bare microphone and say the file has no beep.
        await Promise.race([bus.resume(), new Promise((resolve) => setTimeout(resolve, BUS_RESUME_WAIT_MS))])
        const beeping = bus.running()
        syncBeepRef.current = beeping
        if (!beeping) console.warn('[audio] the sync-beep bus is not running; recording the bare microphone')
        await start(beeping ? bus.stream : (micStream ?? bus.stream))
      })()
      return
    }
    if (step === 'stop') {
      void stop().then(async (accounting) => {
        // The tail is on the wire; give the acknowledgements a moment so a
        // chunk the server never confirmed is resent before the accounting
        // that would otherwise report it missing.
        await waitForAcks(sender, ACK_WAIT_MS)
        connection.current?.sendAudioDone({
          ...accounting,
          ...(sessionRef.current ? { sessionId: sessionRef.current } : {}),
          syncBeep: syncBeepRef.current,
        })
      })
    }
  }, [phase, bus, micStream, recording, start, stop, connection, sender])

  // Any gesture is a chance to start a suspended graph, so the next run can
  // carry the beep even when this one could not.
  useEffect(() => {
    if (!bus) return
    const wake = (): void => void bus.resume()
    window.addEventListener('keydown', wake)
    window.addEventListener('pointerdown', wake)
    return () => {
      window.removeEventListener('keydown', wake)
      window.removeEventListener('pointerdown', wake)
    }
  }, [bus])

  // The anchor. `SYNC_BEEP` arrives in the same frame as `ROUND_STARTED`, so
  // the tone is mixed in within a frame of the log's own tick 0.
  useEffect(
    () =>
      feed.subscribe((snapshot) => {
        if (snapshot.syncBeeps > beepsRef.current) {
          beepsRef.current = snapshot.syncBeeps
          bus?.beep()
        }
      }),
    [feed, bus],
  )
}

/** How often unacknowledged audio chunks are offered again. */
const RESEND_PASS_MS = 1000
/** How long the end of a round waits for the last chunks to be acknowledged. */
const ACK_WAIT_MS = 3000
/** How long a round start waits for the beep bus to start before recording without it. */
const BUS_RESUME_WAIT_MS = 300

/** Resolve once nothing is waiting for an answer, resending as it goes, or at the deadline. */
async function waitForAcks(sender: { pending(): number; resendDue(): number }, deadlineMs: number): Promise<void> {
  const started = Date.now()
  while (sender.pending() > 0 && Date.now() - started < deadlineMs) {
    sender.resendDue()
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
}

/**
 * How often the page asks the feed whether the round is over, on top of asking
 * every time a frame lands. Four times a second is far below anything a player
 * can see and far above the one moment it exists for.
 */
const ROUND_END_POLL_MS = 250

/**
 * The two things the React tree genuinely needs from the feed: whether the
 * round is over, and how it went. Everything else the players look at is drawn
 * — by the canvas, or by the HUD's own animation frame — so this subscription
 * fires a handful of times per round rather than fifty times a second.
 *
 * The subscription is not enough on its own, and that is the round-end bug.
 * `feed.subscribe` fires on `push`, so every transition this page makes is a
 * consequence of a frame arriving — and the transition to the end screen is the
 * one that has no frame after it. If `ROUND_ENDED` is dropped, or arrives on a
 * socket that has just gone quiet, the last thing this page was told is that
 * the round is running, and it goes on believing that for as long as the tab is
 * open. So the feed is also *asked*, on a timer the socket cannot silence: the
 * feed latches the end on its own clock (see `ROUND_END_GRACE_MS`) and this is
 * what notices. The poll stops the moment the round is over — there is nothing
 * left to wait for, and the end screen is not a thing that un-happens.
 */
export function useRoundOutcome(feed: EventFeed): Pick<FeedSnapshot, 'ended' | 'tally'> {
  const [outcome, setOutcome] = useState<Pick<FeedSnapshot, 'ended' | 'tally'>>(() => {
    const snapshot = feed.snapshot()
    return { ended: snapshot.ended, tally: snapshot.tally }
  })

  const read = useCallback((snapshot: FeedSnapshot): void => {
    setOutcome((previous) =>
      previous.ended === snapshot.ended && previous.tally === snapshot.tally
        ? previous
        : { ended: snapshot.ended, tally: snapshot.tally },
    )
  }, [])

  useEffect(() => feed.subscribe(read), [feed, read])

  useEffect(() => {
    if (outcome.ended) return
    const timer = setInterval(() => read(feed.snapshot()), ROUND_END_POLL_MS)
    return () => clearInterval(timer)
  }, [feed, read, outcome.ended])

  return outcome
}

/**
 * Put this tab's voice link on the tape.
 *
 * The server records a `VOICE_STATE` event for every report made during a
 * round, so "we could not hear each other" — a link that never connected, or
 * one that did not come back after a reload — is visible in the data instead of
 * only on two screens. Reported on every change, and again when a round starts,
 * because a report made in the lobby has no round to land in.
 */
export function useVoiceStateReport(
  connection: React.MutableRefObject<Connection | null>,
  status: VoiceLinkState,
  relayed: boolean,
  phase: RoomPhase,
): void {
  const playing = phase === 'playing'
  useEffect(() => {
    if (!playing) return
    connection.current?.reportVoiceState(status, relayed)
  }, [connection, status, relayed, playing])
}
